/**
 * OnboardingWizard — shown once after a new user signs in for the first time.
 * Collects profile data for the CRM. Frictionless: all fields except first name
 * are optional. Saves to /v1/crm/profile on completion.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Building2, Zap, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, Sparkles, Mail, Phone, Globe,
} from "lucide-react";
import { api } from "../api/client";

interface Props {
  email: string;
  onComplete: () => void;
}

const ROLES = [
  { id: "clinician",  label: "Clinician / GP",        icon: "🩺" },
  { id: "researcher", label: "Researcher",             icon: "🔬" },
  { id: "developer",  label: "Developer / Engineer",   icon: "💻" },
  { id: "student",    label: "Student",                icon: "🎓" },
  { id: "other",      label: "Other",                  icon: "👤" },
];

const ORG_TYPES = [
  { id: "nhs_trust",  label: "NHS Trust" },
  { id: "biotech",    label: "Biotech / Pharma" },
  { id: "university", label: "University / Research" },
  { id: "cro",        label: "CRO" },
  { id: "startup",    label: "Startup" },
  { id: "other",      label: "Other" },
];

const USE_CASES = [
  { id: "vitamin_recommendations", label: "Vitamin Recommendations" },
  { id: "literature_qa",           label: "Literature Q&A" },
  { id: "report_generation",       label: "Report Generation" },
  { id: "regulatory_drafting",     label: "Regulatory Drafting" },
  { id: "data_interpretation",     label: "Data Interpretation" },
];

const HEAR_ABOUT = [
  "Google / Search", "LinkedIn", "Colleague / Referral",
  "Conference / Event", "NHS / Academic network", "Other",
];

const STEPS = ["About you", "Your organisation", "Your goals", "Done"];

export default function OnboardingWizard({ email, onComplete }: Props) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    country: "UK",
    role: "",
    org_name: "",
    org_type: "",
    use_cases: [] as string[],
    hear_about: "",
    goals: "",
    newsletter: true,
  });

  const set = (k: keyof typeof form) => (v: string | boolean | string[]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleUseCase = (id: string) => {
    set("use_cases")(
      form.use_cases.includes(id)
        ? form.use_cases.filter((u) => u !== id)
        : [...form.use_cases, id]
    );
  };

  const canNext = () => {
    if (step === 0) return form.first_name.trim().length > 0;
    return true;
  };

  const handleFinish = async () => {
    setSaving(true);
    setError("");
    try {
      await api.crmSaveProfile({ ...form, email, source: "web_onboarding" });
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step) / (STEPS.length - 1)) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(8,12,20,0.95)", backdropFilter: "blur(12px)" }}>

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-ay-cyan/20 border border-ay-cyan/30 flex items-center justify-center">
              <Zap size={16} className="text-ay-cyan" />
            </div>
            <span className="font-bold text-white">
              Alpha<span className="text-ay-cyan">You</span>
              <span className="text-ay-muted font-normal text-sm ml-1">AI</span>
            </span>
          </div>
          {step < 3 && (
            <>
              <h2 className="text-2xl font-black text-white mb-1">
                Welcome to AlphaYou
              </h2>
              <p className="text-ay-muted text-sm">
                Quick setup — takes under 60 seconds
              </p>
            </>
          )}
        </div>

        {/* Progress bar */}
        {step < 3 && (
          <div className="mb-6">
            <div className="flex justify-between text-xs text-ay-muted mb-2">
              {STEPS.slice(0, 3).map((s, i) => (
                <span key={s} className={i <= step ? "text-ay-cyan" : ""}>{s}</span>
              ))}
            </div>
            <div className="h-1 bg-ay-border rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-ay-cyan rounded-full"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </div>
        )}

        {/* Card */}
        <div className="ay-card border border-ay-border">
          <AnimatePresence mode="wait">

            {/* ── Step 0: About you ── */}
            {step === 0 && (
              <motion.div key="step0"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <User size={18} className="text-ay-cyan" />
                  <h3 className="font-bold text-white">Tell us about yourself</h3>
                </div>

                {/* Email (pre-filled, read-only) */}
                <div>
                  <label className="label">Email</label>
                  <div className="input flex items-center gap-2 opacity-60 cursor-not-allowed">
                    <Mail size={14} className="text-ay-muted flex-shrink-0" />
                    <span className="text-sm">{email}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">First name *</label>
                    <input className="input" placeholder="Jane"
                      value={form.first_name}
                      onChange={(e) => set("first_name")(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Last name</label>
                    <input className="input" placeholder="Smith"
                      value={form.last_name}
                      onChange={(e) => set("last_name")(e.target.value)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Phone (optional)</label>
                    <div className="relative">
                      <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ay-muted" />
                      <input className="input pl-8" placeholder="+44 7700 000000"
                        value={form.phone}
                        onChange={(e) => set("phone")(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="label">Country</label>
                    <div className="relative">
                      <Globe size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ay-muted" />
                      <input className="input pl-8" placeholder="UK"
                        value={form.country}
                        onChange={(e) => set("country")(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label">I am a…</label>
                  <div className="grid grid-cols-3 gap-2">
                    {ROLES.map((r) => (
                      <button key={r.id} type="button"
                        onClick={() => set("role")(r.id)}
                        className={`p-2.5 rounded-xl border text-xs font-medium transition-all text-center ${
                          form.role === r.id
                            ? "border-ay-cyan bg-ay-cyan/10 text-ay-cyan"
                            : "border-ay-border text-ay-muted hover:border-ay-cyan/40 hover:text-white"
                        }`}
                      >
                        <div className="text-lg mb-0.5">{r.icon}</div>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Step 1: Organisation ── */}
            {step === 1 && (
              <motion.div key="step1"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={18} className="text-ay-cyan" />
                  <h3 className="font-bold text-white">Your organisation</h3>
                </div>

                <div>
                  <label className="label">Organisation name (optional)</label>
                  <input className="input" placeholder="e.g. NHS Greater Manchester, Sano Genetics…"
                    value={form.org_name}
                    onChange={(e) => set("org_name")(e.target.value)} />
                </div>

                <div>
                  <label className="label">Organisation type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {ORG_TYPES.map((o) => (
                      <button key={o.id} type="button"
                        onClick={() => set("org_type")(o.id)}
                        className={`p-2.5 rounded-xl border text-xs font-medium transition-all ${
                          form.org_type === o.id
                            ? "border-ay-cyan bg-ay-cyan/10 text-ay-cyan"
                            : "border-ay-border text-ay-muted hover:border-ay-cyan/40 hover:text-white"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="label">How did you hear about AlphaYou?</label>
                  <select className="input"
                    value={form.hear_about}
                    onChange={(e) => set("hear_about")(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {HEAR_ABOUT.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Goals ── */}
            {step === 2 && (
              <motion.div key="step2"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={18} className="text-ay-cyan" />
                  <h3 className="font-bold text-white">What will you use AlphaYou for?</h3>
                </div>

                <div>
                  <label className="label">Select all that apply</label>
                  <div className="grid grid-cols-2 gap-2">
                    {USE_CASES.map((u) => {
                      const active = form.use_cases.includes(u.id);
                      return (
                        <button key={u.id} type="button"
                          onClick={() => toggleUseCase(u.id)}
                          className={`p-3 rounded-xl border text-xs font-medium text-left transition-all flex items-center gap-2 ${
                            active
                              ? "border-ay-cyan bg-ay-cyan/10 text-ay-cyan"
                              : "border-ay-border text-ay-muted hover:border-ay-cyan/40 hover:text-white"
                          }`}
                        >
                          <CheckCircle2 size={13} className={active ? "text-ay-cyan" : "opacity-30"} />
                          {u.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="label">Anything else you'd like to achieve? (optional)</label>
                  <textarea className="input resize-none" rows={3}
                    placeholder="e.g. Automate microbiome report generation for 500 patients/month…"
                    value={form.goals}
                    onChange={(e) => set("goals")(e.target.value)}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-ay-cyan"
                    checked={form.newsletter}
                    onChange={(e) => set("newsletter")(e.target.checked)} />
                  <span className="text-xs text-ay-muted leading-relaxed">
                    Send me product updates, new features, and UK clinical AI insights.
                    Unsubscribe anytime.
                  </span>
                </label>

                {error && (
                  <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
              </motion.div>
            )}

            {/* ── Step 3: Done ── */}
            {step === 3 && (
              <motion.div key="step3"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center py-4"
              >
                <div className="w-16 h-16 rounded-full bg-ay-emerald/10 border border-ay-emerald/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 size={28} className="text-ay-emerald" />
                </div>
                <h3 className="text-2xl font-black text-white mb-2">You're all set!</h3>
                <p className="text-ay-muted text-sm mb-6 max-w-xs mx-auto">
                  Welcome to AlphaYou, {form.first_name || "there"}. Your profile has been saved.
                  Let's get started.
                </p>
                <button onClick={onComplete} className="btn-primary mx-auto">
                  Go to dashboard <ArrowRight size={16} />
                </button>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Navigation */}
          {step < 3 && (
            <div className="flex items-center justify-between mt-6 pt-5 border-t border-ay-border">
              <button
                onClick={() => step > 0 ? setStep(step - 1) : onComplete()}
                className="btn-ghost text-sm"
              >
                {step === 0 ? "Skip for now" : <><ArrowLeft size={14} /> Back</>}
              </button>

              {step < 2 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canNext()}
                  className="btn-primary text-sm py-2 px-5"
                >
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={saving}
                  className="btn-primary text-sm py-2 px-5"
                >
                  {saving
                    ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                    : <>Finish <CheckCircle2 size={14} /></>
                  }
                </button>
              )}
            </div>
          )}
        </div>

        {step < 3 && (
          <p className="text-center text-xs text-ay-muted mt-4">
            Your data is stored securely in the UK · GDPR compliant · Never shared
          </p>
        )}
      </motion.div>
    </div>
  );
}
